import dayjs from 'dayjs';
import useAxios from 'hooks/useAxios';

const EPOCH_START = dayjs().date(1).month(0).year(2000).startOf('day').toISOString();

/**
 * Shared helper: builds common date + entity filter params from a filters object.
 * Eliminates the repeated 20-line block across getStatistics, ggrForPeriod, etc.
 */
function buildParams(filters = {}, extras = {}) {
  const params = {
    startDate: filters.startDate ? dayjs(filters.startDate).toISOString() : EPOCH_START,
    endDate: filters.endDate ? dayjs(filters.endDate).toISOString() : dayjs().endOf('day').toISOString()
  };
  if (filters.clientId) params.clientId = filters.clientId;
  if (filters.operatorId) params.operatorId = filters.operatorId;
  if (filters.brandId) params.brandId = filters.brandId;
  if (filters.gameType) params.gameType = filters.gameType;
  if (filters.gameCategory) params.gameCategory = filters.gameCategory;
  return { ...params, ...extras };
}

export const getEventsHistory = async (limit = 20, page = 1, filters = '') => {
  const api = useAxios();
  try {
    let timeFilter = [];
    const params = {
      limit: limit,
      page: page,
      sortBy: 'createdAt:DESC'
    };

    if (Object.keys(filters).length) {
      if (filters.users.length) {
        params['filter.user.id'] = `$in:${filters.users.map((u) => u.id).toString()}`;
      }
      if (filters.eventTypes.length) {
        params['filter.type.id'] = `$in:${filters.eventTypes.map((e) => e.id).toString()}`;
      }
      if (filters.casinos.length) {
        params['filter.casino.id'] = `$in:${filters.casinos.map((c) => c.id).toString()}`;
      }
      if (filters.countries.length) {
        params['filter.country'] = `$in:${filters.countries.map((c) => c).toString()}`;
      }
      if (filters.from) {
        timeFilter.push(`$gte:${filters.from.format('YYYY-MM-DDTHH:mm:ssZ')}`);
      }
      if (filters.to) {
        timeFilter.push(`$lte:${filters.to.format('YYYY-MM-DDTHH:mm:ssZ')}`);
      }
      if (filters.demo) {
        params['filter.user.isDemo'] = `true`;
      } else {
        params['filter.user.isDemo'] = `false`;
      }
    }
    if (timeFilter.length) {
      params['filter.createdAt'] = timeFilter;
    }
    const res = await api.get('/admin/metrics', {
      params: params
    });

    const data = res.data.data.map((event) => {
      return {
        ...event,
        casino: event?.casino?.name ? event.casino.name : '-',
        username: event?.user?.nickname != null ? event.user.nickname : '-',
        timestamp: event?.createdAt ? event.createdAt : '-',
        event_type: event?.type?.name ? event.type.name : '-',
        event_type_id: event?.type?.id ? event.type.id : '-',
        amount: event?.payload?.amount != null ? event.payload.amount : '-',
        gameType: event?.payload?.gameType ? event.payload.gameType : '-',
        country: event?.country ? event.country : '-'
      };
    });
    return {
      data: data,
      meta: res.data.meta
    };
  } catch (err) {
    console.error(err);
    return {
      data: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0
      }
    };
  }
};

export const getEventsAggregated = async (limit = 20, page = 1, filters = null) => {
  const api = useAxios();
  try {
    const params = {
      limit: limit,
      page: page,
      sortBy: 'createdAt:DESC'
    };

    if (Object.keys(filters).length) {
      if (filters.eventTypes.length) {
        params['id'] = `${filters.eventTypes.map((e) => e.id).toString()}`;
      }
    }
    const res = await api.get('/admin/metrics/aggregated', {
      params: params
    });

    const data = res.data.map((event) => {
      return {
        event_type: event?.name ? event.name : '-',
        calls: event?.count ? event.count : '-',
        user_calls: event?.userCount ? event.userCount : '-',
        avg_daily_calls: Math.floor(event.count / 30),
        id: event.id
      };
    });
    return {
      data: data,
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0
      } //res.data.meta
    };
  } catch (err) {
    console.error(err);
    return {
      data: [],
      meta: {
        totalItems: 0,
        itemCount: 0,
        itemsPerPage: 0,
        totalPages: 0,
        currentPage: 0
      }
    };
  }
};

export const getTodayNumbers = async (type) => {
  try {
    const api = useAxios();
    let date = new Date();
    date.setHours(0, 0, 0, 0);
    date = date.toJSON();

    let dateTomorrow = new Date();
    dateTomorrow.setHours(24, 0, 0, 0);
    dateTomorrow = dateTomorrow.toJSON();

    const res = await api.get(`admin/metrics/analytics-count-for-period/${type}`, {
      params: {
        limit: 100000,
        startDate: date,
        endDate: dateTomorrow
      }
    });

    return await res.data;
  } catch (error) {
    console.error(error);

    return {
      data: []
    };
  }
};

export const getChartAmount = async (type, filters = {}) => {
  // Determine appropriate interval based on date range
  const getIntervalForRange = (startDate, endDate) => {
    if (!startDate || !endDate) return 'month';
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const daysDiff = end.diff(start, 'day');

    if (daysDiff <= 1) return 'hour'; // Today/Yesterday: hourly
    if (daysDiff <= 14) return 'day'; // Up to 2 weeks: daily
    if (daysDiff <= 90) return 'week'; // Up to 3 months: weekly
    return 'month'; // Longer periods: monthly
  };

  const interval = getIntervalForRange(filters.startDate, filters.endDate);

  const params = {
    limit: 20,
    type: type,
    interval: interval
  };

  const api = useAxios();
  try {
    // Handle date parameters
    if (!filters.startDate) {
      params.startDate = dayjs(new Date()).subtract(12, 'month').toISOString();
    } else if (filters.startDate instanceof Date) {
      params.startDate = filters.startDate.toISOString();
    } else {
      params.startDate = filters.startDate;
    }

    if (!filters.endDate) {
      params.endDate = new Date().toISOString();
    } else if (filters.endDate instanceof Date) {
      params.endDate = filters.endDate.toISOString();
    } else {
      params.endDate = filters.endDate;
    }

    // Handle entity filters
    if (filters.clientId) params.clientId = filters.clientId;
    if (filters.operatorId) params.operatorId = filters.operatorId;
    if (filters.brandId) params.brandId = filters.brandId;
    if (filters.gameType) params.gameType = filters.gameType;
    if (filters.gameCategory) params.gameCategory = filters.gameCategory;

    const res = await api.get('metric/chartAmount/', { params });

    if (!res.data || !Array.isArray(res.data) || res.data.length === 0) {
      return { data: [], interval };
    }

    // Return full date info so frontend can format labels based on interval
    const data = res.data.map((item) => {
      return {
        date: item?.intervalStart ? new Date(item.intervalStart) : null,
        amount: item?.amount ? item.amount : 0
      };
    });

    return { data, interval };
  } catch (err) {
    console.error(err);
    return { data: [], interval };
  }
};

export const getNewRegistrations = async () => {
  const api = useAxios();
  try {
    let startDate = dayjs(new Date()).subtract(12, 'month')['$d'].toISOString();
    let endDate = new Date().toISOString();

    const params = {
      limit: 20,
      type: 'registration_start',
      interval: 'month',
      startDate: startDate,
      endDate: endDate
    };

    const res = await api.get('admin/metrics/metrics-by-interval/', {
      params: params
    });

    const data = res.data.map((registrations) => {
      return {
        month: registrations?.intervalStart ? new Date(registrations.intervalStart).getMonth() + 1 : '-',
        count: registrations?.count ? registrations.count : '-'
      };
    });

    return data;
  } catch (err) {
    console.error(err);
    return [
      {
        month: '-',
        count: 0
      }
    ];
  }
};

export const getGameSessions = async () => {
  try {
    const api = useAxios();

    // let dateFrom = dayjs();
    // dateFrom = dateFrom.toJSON();

    // let dateTo = dayjs().hour(23).minute(59);
    // dateTo = dateTo.toJSON();

    let dateFrom = dayjs().startOf('day').toJSON();
    let dateTo = dayjs().endOf('day').toJSON();

    const res = await api.get(`/metric/chartAmount`, {
      params: {
        limit: 1,
        startDate: dateFrom,
        endDate: dateTo,
        type: `bet`,
        interval: 'day'
      }
    });

    const amount = res.data[0].amount;

    return [amount];
  } catch (error) {
    console.error(error);
    return [0, 0, 0];
  }
};

export const trackSuccessfulLogins = async () => {
  try {
    const api = useAxios();

    const res = await api.get(`admin/metrics`, {
      params: {
        limit: 8,
        'filter.type.id': `$in:38`,
        sortBy: 'createdAt:DESC'
      }
    });

    const data = res.data.data.map((login) => {
      let date = new Date(login.createdAt);
      return {
        username: login.user?.nickname ? login.user.nickname : '-',
        date: login.createdAt ? date.toLocaleString() : '-'
      };
    });

    return data;
  } catch (error) {
    console.error(error);
    return {};
  }
};

export const getStatistics = async (filters = {}) => {
  try {
    const api = useAxios();
    const res = await api.get(`/metric/statistics`, { params: buildParams(filters) });
    return res;
  } catch (error) {
    console.error(error);
    return { data: [] };
  }
};

export const getEmailSummary = async (filters = {}) => {
  try {
    const api = useAxios();
    const res = await api.get(`/metric/email-metric-summary-for-period`, { params: buildParams(filters) });
    return res;
  } catch (error) {
    console.error(error);
    return { data: [] };
  }
};

export const ggrForPeriod = async (filters = {}) => {
  try {
    const api = useAxios();
    const res = await api.get(`/metric/ggrForPeriod`, { params: buildParams(filters) });
    return res;
  } catch (error) {
    console.error(error);
    return { data: [] };
  }
};

export const ggrForPeriodAverage = async (filters = {}) => {
  try {
    const api = useAxios();
    const res = await api.get(`/metric/averageForPeriod`, { params: buildParams(filters) });
    return res;
  } catch (error) {
    console.error(error);
    return { data: [] };
  }
};

export const getCountryChart = async (filters = {}) => {
  try {
    const api = useAxios();
    const res = await api.get(`session/countryChart`, { params: buildParams(filters) });
    return res;
  } catch (error) {
    console.error(error);
    return { data: [] };
  }
};

/**
 * Get dashboard metrics for the specified period and filters.
 * @param {Object} filters - Filter parameters
 * @param {string} filters.startDate - Start date (ISO string or Date)
 * @param {string} filters.endDate - End date (ISO string or Date)
 * @param {string} [filters.clientId] - Filter by client/integration ID
 * @param {string} [filters.operatorId] - Filter by operator ID
 * @param {string} [filters.brandId] - Filter by brand ID
 * @param {string} [filters.gameType] - Filter by game type
 * @param {string} [filters.gameCategory] - Filter by game category (pvp/slot)
 * @returns {Promise<Object>} Dashboard metrics response
 */
export const getDashboardMetrics = async (filters = {}) => {
  try {
    const api = useAxios();
    const params = buildParams(filters);
    const res = await api.get('/metric/dashboard', { params });

    return {
      success: true,
      data: res.data,
      meta: {
        startDate: params.startDate,
        endDate: params.endDate,
        filters: {
          clientId: filters.clientId || null,
          operatorId: filters.operatorId || null,
          brandId: filters.brandId || null,
          gameType: filters.gameType || null,
          gameCategory: filters.gameCategory || null
        }
      }
    };
  } catch (error) {
    console.error('getDashboardMetrics error:', error);
    return {
      success: false,
      data: {
        ggr: 0,
        ngr: 0,
        totalBets: { count: 0, amount: 0 },
        totalWins: { count: 0, amount: 0 },
        roundsPlayed: 0,
        averageBetSize: 0,
        uniquePlayers: 0,
        totalSessions: 0,
        averageSessionLength: 0,
        roundsPerSession: 0
      },
      meta: null,
      error: error.message
    };
  }
};

/**
 * Get PvP-specific metrics for the specified period and filters.
 * @param {Object} filters - Filter parameters (same as getDashboardMetrics)
 * @returns {Promise<Object>} PvP metrics response
 */
export const getPvPMetrics = async (filters = {}) => {
  try {
    const api = useAxios();
    const params = buildParams(filters);
    const res = await api.get('/metric/pvp', { params });

    return {
      success: true,
      data: res.data,
      meta: {
        startDate: params.startDate,
        endDate: params.endDate
      }
    };
  } catch (error) {
    console.error('getPvPMetrics error:', error);
    return {
      success: false,
      data: {
        ggr: 0,
        ngr: 0,
        totalBets: { count: 0, amount: 0 },
        totalWins: { count: 0, amount: 0 },
        roundsPlayed: 0,
        averageBetSize: 0,
        uniquePlayers: 0,
        totalSessions: 0,
        averageSessionLength: 0,
        roundsPerSession: 0,
        rolledBackBets: { count: 0, amount: 0, percentageOfTotal: 0 },
        rakeCollected: 0
      },
      meta: null,
      error: error.message
    };
  }
};

/**
 * Get Slots-specific metrics for the specified period and filters.
 * @param {Object} filters - Filter parameters (same as getDashboardMetrics)
 * @returns {Promise<Object>} Slots metrics response
 */
export const getSlotsMetrics = async (filters = {}) => {
  try {
    const api = useAxios();
    const params = buildParams(filters);
    const res = await api.get('/metric/slots', { params });

    return {
      success: true,
      data: res.data,
      meta: {
        startDate: params.startDate,
        endDate: params.endDate
      }
    };
  } catch (error) {
    console.error('getSlotsMetrics error:', error);
    return {
      success: false,
      data: {
        ggr: 0,
        ngr: 0,
        totalBets: { count: 0, amount: 0 },
        totalWins: { count: 0, amount: 0 },
        roundsPlayed: 0,
        averageBetSize: 0,
        uniquePlayers: 0,
        totalSessions: 0,
        averageSessionLength: 0,
        roundsPerSession: 0,
        actualRtp: 0,
        theoreticalRtp: null,
        rtpVariance: null,
        rtpByGame: []
      },
      meta: null,
      error: error.message
    };
  }
};

/**
 * Get per-game summary statistics for the specified period and filters.
 * @param {Object} filters - Filter parameters (same as getDashboardMetrics)
 * @returns {Promise<Object>} Game summary response
 */
export const getGameSummary = async (filters = {}) => {
  try {
    const api = useAxios();
    const params = buildParams(filters, {
      aggregateByName: filters.aggregateByName !== false
    });

    const res = await api.get('/metric/game-summary-for-period', { params });

    return {
      success: true,
      data: res.data,
      meta: {
        startDate: params.startDate,
        endDate: params.endDate
      }
    };
  } catch (error) {
    console.error('getGameSummary error:', error);
    return {
      success: false,
      data: { games: [] },
      meta: null,
      error: error.message
    };
  }
};

/**
 * Get multiplayer liquidity and matchmaking report data.
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} multiplayer liquidity report response
 */
export const getMultiplayerLiquidityReport = async (filters = {}) => {
  try {
    const api = useAxios();
    const baseParams = buildParams(filters);
    // Override default startDate for multiplayer (last 30 days instead of epoch)
    if (!filters.startDate) baseParams.startDate = dayjs().subtract(30, 'day').startOf('day').toISOString();
    const extras = {};
    if (filters.timezone) extras.timezone = filters.timezone;
    if (filters.maxSessionMinutes) extras.maxSessionMinutes = filters.maxSessionMinutes;
    if (filters.bucketMinutes) extras.bucketMinutes = filters.bucketMinutes;
    if (filters.breakdownLimit) extras.breakdownLimit = filters.breakdownLimit;
    const params = { ...baseParams, ...extras };

    const res = await api.get('/metric/multiplayer-liquidity-report', { params });
    return {
      success: true,
      data: res.data
    };
  } catch (error) {
    console.error('getMultiplayerLiquidityReport error:', error);
    return {
      success: false,
      data: {
        summary: {
          sessionsTotal: 0,
          uniquePlayers: 0,
          sessionsWithBet: 0,
          sessionsWithoutBet: 0,
          playRatePct: 0,
          noBetRatePct: 0,
          joinedAloneSameBetlimitPct: 0,
          avgJoinConcurrencySameBetlimit: 0,
          sessionsWithoutTableId: 0,
          noBetUnder30s: 0,
          noBetUnder30sRatePct: 0,
          avgDurationMs: 0,
          avgFlightTimeMs: 0,
          peakConcurrentPlayers: 0
        },
        daily: [],
        hourly: [],
        operators: [],
        providers: [],
        dropoffReasons: [],
        minPlayersPressure: [],
        period: null
      },
      error: error.message
    };
  }
};

